import { Select } from 'antd';
import { DefaultOptionType } from 'antd/lib/select';
import React from 'react';

import { L } from '@lib';
import { CalendarStatusStyle, CalendarStatusStyleObjectKey } from '@models';
import './OrderStatusSelect.css';

const { Option } = Select;

/** A status's name with a dot in the status's colour in front of it. */
const StatusLabel: React.FC<{ status: CalendarStatusStyleObjectKey; children: React.ReactNode }> = ({ status, children }) => (
  <span style={{ display: 'inline-flex', alignItems: 'center' }}>
    <i className="status-option-dot" style={{ background: CalendarStatusStyle[status].main }} />
    {children}
  </span>
);

interface IProps {
  disabled?: boolean;
  hideFirstOption?: boolean;
  hidePaid?: boolean;
  hideReady?: boolean;
  hideApproval?: boolean;
  mode?: 'multiple';
  status?: string | string[];
  className?: string;
  showCancel?: boolean;
  /** Cancelling has its own "Cancel job" button; the list then offers it only to show a job that already is. */
  hideCancel?: boolean;
  style?: React.CSSProperties | undefined;
  width?: string | number;
  size?: 'small' | 'middle' | 'large';
  onChange?:
    | ((value: any, option: DefaultOptionType | DefaultOptionType[] | undefined) => void)
    | undefined;
}

export const OrderStatusSelect: React.FC<IProps> = ({
  disabled = false,
  showCancel = false,
  hideCancel = false,
  hideFirstOption = false,
  hidePaid = false,
  hideReady = false,
  hideApproval = false,
  mode,
  status,
  style = { minWidth: 200 },
  width,
  className,
  size = 'middle',
  onChange,
}) => {
  const isMultiple = mode === 'multiple';

  const canCancelFromList =
    showCancel ||
    isMultiple ||
    (typeof status === 'string' &&
      status !== 'Completed' &&
      status !== 'Ready' &&
      status !== 'Paid');
  const showCancelOption = hideCancel ? status === 'Canceled' : canCancelFromList;

  // "Estimate" is shown when not explicitly hidden, or when the order is already in it.
  const showApprovalOption = !hideApproval || status === 'Approval';

  // "To do", "Ready to go" and "Estimate approved" are Booked now, and "Parts not ordered" / "Parts
  // ordered" are On hold (the migration moves the data). A row it has not reached yet is shown as
  // the status it became, selected on the one option, rather than as an extra option or an empty box.
  const displayStatus =
    status === 'Todo' || status === 'ReadyToGo' || status === 'Approved'
      ? 'Booked'
      : status === 'PartsNotOrdered' || status === 'PartsOrdered'
        ? 'OnHold'
        : status;

  const mergedStyle = {
    ...style,
    ...(width && { width, minWidth: 'auto' }), // Override width and remove minWidth when width is provided
  };

  // A job's own status colours the select; a filter (several statuses, or "All") stays neutral.
  const statusStyle = isMultiple ? undefined : CalendarStatusStyle[displayStatus as CalendarStatusStyleObjectKey];
  const colourVars = statusStyle
    ? ({
        '--status-main': statusStyle.main,
        '--status-soft': statusStyle.soft,
        '--status-card': statusStyle.card,
      } as React.CSSProperties)
    : undefined;

  return (
    <Select
      mode={mode}
      disabled={disabled}
      className={[className, statusStyle ? 'order-status-select' : ''].filter(Boolean).join(' ')}
      style={{ ...mergedStyle, ...colourVars }}
      size={size}
      allowClear={isMultiple}
      maxTagCount={isMultiple ? 'responsive' : undefined}
      placeholder={isMultiple ? L('All') : undefined}
      defaultValue={isMultiple ? undefined : 'All'}
      value={displayStatus}
      onChange={onChange}
    >
      {!isMultiple && !hideFirstOption && <Option value={undefined}>{L('All')}</Option>}
      {showApprovalOption && <Option value="Approval"><StatusLabel status="Approval">{L('Estimate')}</StatusLabel></Option>}
      <Option value="Booked"><StatusLabel status="Booked">{L('Booked')}</StatusLabel></Option>
      {/* A draft estimate only leaves draft by being sent for approval (the job card's button), so
          here it only shows where the job is; in a filter it can be picked. */}
      {(isMultiple || status === 'Draft') && (
        <Option value="Draft" disabled={!isMultiple}>
          <StatusLabel status="Draft">{L('Draft')}</StatusLabel>
        </Option>
      )}
      {/* In progress is the job card's Start button, not a choice: in a filter it can be picked, on
          a job it only shows where the job is (greyed). */}
      {(isMultiple || status === 'InProgress') && (
        <Option value="InProgress" disabled={!isMultiple}>
          <StatusLabel status="InProgress">{L('In progress')}</StatusLabel>
        </Option>
      )}
      <Option value="OnHold"><StatusLabel status="OnHold">{L('On hold')}</StatusLabel></Option>
      {showCancelOption && <Option value="Canceled"><StatusLabel status="Canceled">{L('Cancelled')}</StatusLabel></Option>}
      <Option value="Completed"><StatusLabel status="Completed">{L('Completed')}</StatusLabel></Option>
      {!hideReady && <Option value="Ready"><StatusLabel status="Ready">{L('Ready for payment')}</StatusLabel></Option>}
      {!hidePaid && <Option value="Paid"><StatusLabel status="Paid">{L('Paid')}</StatusLabel></Option>}
    </Select>
  );
};
